import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Tooltip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  ClickAwayListener,
  TextField,
  Chip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { getAllPredefinedText } from "../API/PredefinedTextApi";
import { getAutocompleteSuggestions, logAutocompleteInteraction } from "../API/AutocompleteApi";
import AuthContext from "../Context/AuthProvider";
import { useSnackbar } from "../Utility/UserMessageContext";

const NotesTextField = ({
  value,
  onChange,
  label = "Enter your notes",
  disabled = false,
  rows = 8,
  noteStatus = "Draft",
  tagsForAutocomplete = []
}) => {
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(AuthContext);
  
  // Refs for autocomplete
  const textFieldRef = useRef(null);
  const autocompleteRef = useRef(null);
  const selectedItemRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // States for predefined text functionality
  const [openTextOptionsDialog, setOpenTextOptionsDialog] = useState(false);
  const [selectedTextOptions, setSelectedTextOptions] = useState([]);
  const [predefinedTexts, setPredefinedTexts] = useState([]);
  const [loadingPredefinedTexts, setLoadingPredefinedTexts] = useState(false);
  
  // States for autocomplete functionality
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [autocompleteSuggestions, setAutocompleteSuggestions] = useState([]);
  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [autocompletePosition, setAutocompletePosition] = useState({ top: 0, left: 0 });
  const [currentSentence, setCurrentSentence] = useState("");
  const [cursorPosition, setCursorPosition] = useState(0);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);

  // Debug effect to track selectedSuggestionIndex changes
  useEffect(() => {
    console.log('selectedSuggestionIndex changed to:', selectedSuggestionIndex);
  }, [selectedSuggestionIndex]);

  // Auto-scroll selected item into view
  useEffect(() => {
    if (selectedSuggestionIndex >= 0 && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [selectedSuggestionIndex]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Autocomplete API call with demo data
  const fetchAutocompleteSuggestions = async (querySentence, cursorPos) => {

    try {
      setLoadingAutocomplete(true);

      // If we have auth and API is available, try the real API first
      if (auth && auth.selectedFacility?.id) {
        try {
          const result = await getAutocompleteSuggestions(auth, querySentence, tagsForAutocomplete, 20, 1);
          
          if (result.isSuccess && result.data?.items && result.data.items.length > 0) {
            return result.data.items.map(suggestion => ({
              id: suggestion.id || suggestion.suggestion,
              //suggestion: suggestion.title || suggestion.suggestion,
              content: suggestion.suggestedText,
              tags: suggestion.tags,
              score: suggestion.score || 0
            }));
          }
        } catch (apiError) {
          console.warn('API not available, using demo data:', apiError.message);
        }
      }
      
      // Use demo data as fallback or primary source
      
    } catch (error) {
      console.error('Error fetching autocomplete suggestions:', error);
      return [];
    } finally {
      setLoadingAutocomplete(false);
    }
  };

  // Get cursor position and calculate autocomplete position
  const getCursorCoordinates = (input, selectionPoint) => {
    const div = document.createElement('div');
    const span = document.createElement('span');
    const copyStyle = getComputedStyle(input);
    
    for (const prop of copyStyle) {
      div.style[prop] = copyStyle[prop];
    }
    
    div.style.position = 'absolute';
    div.style.left = '-9999px';
    div.style.top = '-9999px';
    div.style.visibility = 'hidden';
    div.style.whiteSpace = 'pre-wrap';
    div.style.wordWrap = 'break-word';
    
    const textContent = input.value.substring(0, selectionPoint);
    div.textContent = textContent;
    span.textContent = '|';
    div.appendChild(span);
    
    document.body.appendChild(div);
    
    const coordinates = {
      top: span.offsetTop + parseInt(copyStyle.borderTopWidth),
      left: span.offsetLeft + parseInt(copyStyle.borderLeftWidth)
    };
    
    document.body.removeChild(div);
    return coordinates;
  };

  // Handle autocomplete trigger
  const handleAutocompleteCheck = async (inputValue, selectionStart) => {
    // Find the start of current sentence (after fullstop or start of text or newline)
    const textBeforeCursor = inputValue.substring(0, selectionStart);
    
    // Split by fullstop or newline to get current sentence
    const sentences = textBeforeCursor.split(/[.\n]+/);
    const sentence = sentences[sentences.length - 1].trim();
    setCurrentSentence(sentence);
    setCursorPosition(selectionStart);

    const suggestions = await fetchAutocompleteSuggestions(
      sentence,
      selectionStart,
    );

    if (suggestions?.length > 0) {
      setAutocompleteSuggestions(suggestions);
      setSelectedSuggestionIndex(-1);

      // Calculate position for autocomplete dropdown
      if (textFieldRef.current) {
        const textInput = textFieldRef.current.querySelector("textarea");
        if (textInput) {
          const coords = getCursorCoordinates(textInput, selectionStart);
          const rect = textInput.getBoundingClientRect();
          setAutocompletePosition({
            top: coords.top + 25, // Add some offset below the cursor
            left: coords.left + 10,
          });
          setShowAutocomplete(true);
        }
      }
    } else {
      setShowAutocomplete(false);
    }
  };

  // Handle text changes with autocomplete
  const handleTextChange = (e) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Reset selection index when typing
    setSelectedSuggestionIndex(-1);
    
    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce timer for 1 second
    const selectionStart = e.target.selectionStart;
    debounceTimerRef.current = setTimeout(() => {
      handleAutocompleteCheck(newValue, selectionStart);
    }, 1000);
  };

  // Handle key navigation in autocomplete
  const handleKeyDown = (e) => {
    console.log('Key pressed:', e.key, 'showAutocomplete:', showAutocomplete, 'suggestions length:', autocompleteSuggestions.length);
    
    if (!showAutocomplete || autocompleteSuggestions.length === 0) {
      return; // Allow normal text field behavior
    }

    const suggestionsCount = autocompleteSuggestions.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        e.stopPropagation();
        console.log('ArrowDown pressed, current index:', selectedSuggestionIndex);
        setSelectedSuggestionIndex(prev => {
          const newIndex = prev < suggestionsCount - 1 ? prev + 1 : 0;
          console.log('Setting new index:', newIndex);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        e.stopPropagation();
        console.log('ArrowUp pressed, current index:', selectedSuggestionIndex);
        setSelectedSuggestionIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : suggestionsCount - 1;
          console.log('Setting new index:', newIndex);
          return newIndex;
        });
        break;
      case 'Enter':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Enter pressed, selecting index:', selectedSuggestionIndex);
          handleSuggestionSelect(autocompleteSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Tab':
        if (selectedSuggestionIndex >= 0) {
          e.preventDefault();
          e.stopPropagation();
          console.log('Tab pressed, selecting index:', selectedSuggestionIndex);
          handleSuggestionSelect(autocompleteSuggestions[selectedSuggestionIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        console.log('Escape pressed, closing autocomplete');
        setShowAutocomplete(false);
        setSelectedSuggestionIndex(-1);
        break;
      default:
        // For other keys, reset selection and allow normal behavior
        if (selectedSuggestionIndex >= 0) {
          setSelectedSuggestionIndex(-1);
        }
        break;
    }
  };

  // Highlight matching text in suggestions
  const highlightMatchingText = (text, words) => {
    //const words 
    if (!words || words.length === 0 || !text) return text;
    
    // Create regex pattern for all words
    const wordPatterns = words.map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // Escape special regex chars
    const regex = new RegExp(`(${wordPatterns.join('|')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => {
      const isMatch = words.some(word => 
        part.toLowerCase() === word.toLowerCase()
      );
      
      return isMatch ? (
        <span key={index} style={{ fontWeight: 'bold', color: '#1976d2' }}>
          {part}
        </span>
      ) : (
        part
      );
    });
  };

  // Handle suggestion selection
  const handleSuggestionSelect = async (suggestion) => {
    // Find the start of current sentence before cursor
    const textBeforeCursor = value.substring(0, cursorPosition);
    const sentences = textBeforeCursor.split(/[.\n]+/);
    const currentSentenceStart = textBeforeCursor.lastIndexOf(sentences[sentences.length - 1]);
    
    const beforeCurrentSentence = value.substring(0, currentSentenceStart);
    const afterCursor = value.substring(cursorPosition);
    const newText = beforeCurrentSentence + suggestion.content + afterCursor;
    
    onChange(newText);
    setShowAutocomplete(false);
  };

  // Close autocomplete when clicking outside
  const handleClickAway = () => {
    setShowAutocomplete(false);
  };

  // Load predefined texts
  const loadPredefinedTexts = async () => {
    if (!auth || !auth.selectedFacility?.id) return;
    
    setLoadingPredefinedTexts(true);
    try {
      const result = await getAllPredefinedText(auth);
      if (result?.isSuccess && result?.data) {
        setPredefinedTexts(result.data);
      }
    } catch (error) {
      console.error('Error loading predefined texts:', error);
      showSnackbar("Error loading predefined texts", "error");
    } finally {
      setLoadingPredefinedTexts(false);
    }
  };

  // Open predefined text dialog
  const handleOpenTextOptions = async () => {
    setOpenTextOptionsDialog(true);
    await loadPredefinedTexts();
  };

  // Close predefined text dialog
  const handleCloseTextOptions = () => {
    setOpenTextOptionsDialog(false);
    setSelectedTextOptions([]);
  };

  // Handle checkbox change in predefined text dialog
  const handleTextOptionChange = (index) => {
    setSelectedTextOptions((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  // Generate text from selected options
  const generateSelectedText = () => {
    const selectedTexts = selectedTextOptions.map((index) => {
      return predefinedTexts[index].content;
    });
    return selectedTexts.join("\\n");
  };

  // Handle add functionality (appends to existing text)
  const handleAdd = () => {
    if (selectedTextOptions.length === 0) {
      showSnackbar("Please select at least one option", "warning");
      return;
    }

    const selectedText = generateSelectedText();
    let newText;
    
    if (value.trim() === "") {
      // If field is empty, just add the selected text
      newText = selectedText;
    } else {
      // If field has content, add selected text with single line break
      newText = value + "\\n" + selectedText;
    }
    
    onChange(newText);
    handleCloseTextOptions();
    showSnackbar("Predefined text added to notes", "success");
  };

  return (
    <>
      {/* Main Text Field with Autocomplete */}
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: "relative" }}>
          <TextField
            ref={textFieldRef}
            label={label}
            value={value}
            onChange={handleTextChange}
            multiline
            rows={rows}
            disabled={disabled}
            variant="outlined"
            fullWidth
            margin="dense"
            size="small"
            inputProps={{
              onKeyDown: handleKeyDown, // Attach keydown to the input element directly
            }}
            sx={{
              backgroundColor: "white",
              "& .MuiInputBase-root": {
                paddingBottom: "48px", // Add space for the icon button
              },
            }}
          />

          {/* Autocomplete Dropdown */}
          {showAutocomplete && (
            <Paper
              ref={autocompleteRef}
              elevation={8}
              sx={{
                position: 'absolute',
                top: autocompletePosition.top,
                left: autocompletePosition.left,
                minWidth: 200,
                maxWidth: 400,
                maxHeight: 200,
                overflow: 'auto',
                zIndex: 9999,
                border: '1px solid #e0e0e0',
              }}
            >
              {loadingAutocomplete ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <CircularProgress size={20} />
                  <Typography variant="caption" sx={{ ml: 1 }}>
                    Loading suggestions...
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {autocompleteSuggestions.map((suggestion, index) => (
                    <ListItem
                      key={suggestion.id || index}
                      ref={selectedSuggestionIndex === index ? selectedItemRef : null}
                      button
                      onClick={() => handleSuggestionSelect(suggestion)}
                      sx={{
                        backgroundColor: selectedSuggestionIndex === index 
                          ? 'rgba(25, 118, 210, 0.12)' 
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedSuggestionIndex === index
                            ? 'rgba(25, 118, 210, 0.16)'
                            : 'rgba(25, 118, 210, 0.08)',
                        },
                        cursor: 'pointer',
                        borderRadius: 1,
                        margin: '2px 4px',
                      }}
                    >
                      <ListItemText
                        primary={suggestion.suggestion}
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary" component="div">
                              {highlightMatchingText(suggestion.content, currentSentence.split(' '))}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {suggestion.tags && suggestion.tags.length > 0 && suggestion.tags.map((tag, tagIndex) => (
                                <Chip
                                  key={tagIndex}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    height: '18px',
                                    fontSize: '0.65rem',
                                    fontWeight: 'medium',
                                    borderRadius: '9px',
                                    '& .MuiChip-label': {
                                      px: 0.75,
                                      py: 0
                                    }
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>
                        }
                        primaryTypographyProps={{
                          variant: 'subtitle2',
                          fontWeight: 'bold',
                        }}
                        secondaryTypographyProps={{
                          component: 'div',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>

      {/* Predefined Text Options Dialog */}
      <Dialog
        open={openTextOptionsDialog}
        onClose={handleCloseTextOptions}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: "60vh" },
        }}
      >
        <DialogTitle>
          <Typography variant="body1">
            Select Predefined Text Options
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose one or more options to add to your notes
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingPredefinedTexts ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading predefined texts...
              </Typography>
            </Box>
          ) : predefinedTexts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No predefined texts available. Please add some predefined texts
                first.
              </Typography>
            </Box>
          ) : (
            <FormGroup>
              {predefinedTexts.map((option, index) => {
                return (
                  <FormControlLabel
                    key={option.id || index}
                    control={
                      <Checkbox
                        checked={selectedTextOptions.includes(index)}
                        onChange={() => handleTextOptionChange(index)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "bold", lineHeight: 1.2 }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ lineHeight: 1.4, mt: 0.5 }}
                        >
                          {option.content}
                        </Typography>
                      </Box>
                    }
                    sx={{
                      mb: 2,
                      alignItems: "flex-start",
                      "& .MuiFormControlLabel-label": {
                        pt: 0.5,
                      },
                    }}
                  />
                );
              })}
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleCloseTextOptions} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            color="primary"
            disabled={
              selectedTextOptions.length === 0 ||
              noteStatus === "Final" ||
              disabled
            }
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotesTextField;